import React, {useContext, useEffect, useState} from "react";
import {AppFunctionsContext} from "../../contexts/AppFunctionsContext";
import {OrderContext} from "../../contexts/OrderContext";
import {OverviewData} from "../../rest/jtmc/models/OverviewData";
import AppConfigContext from "../../contexts/AppConfigContext";
import {CartContext} from "../../contexts/CartContext";
import {PaymentTags} from "../../types";
import {ColumnSpace} from "../../themes/baran/components/_CommonComponents";
import {threeDigitsSep} from "../../helpers";

export type FinancialData = {
    cartPrice: number,
    deliveryPrice: number,
    // Todo: Apply discount code
    cartDiscount: number,
    tax: number,
    ipgWage: number,
    totalOrderPrice: number,
    packingPrice: number,
    discountCodeAmount: number,
    totalOrderPriceConsideringIpgWage: number,
}

export type PaymentOptions = {
    tag: PaymentTags,
    title: string,
    description: string,
    icon: string,
    visible: boolean
}[];

export type DeliveryOptions = {
    tag: string,
    title: string,
    description: string,
    visible: boolean
}[];

export type FinancialRow = {
    title: string,
    price: string,
    visible: boolean
};

export type FinancialDataStrings = {
    rows: FinancialRow[],
    totalPayablePrice: string
}

export default function OverviewScreenFunctions(props: {
    children: any,
    selectedPaymentMethod: "ipg" | "ctc" | "pos" | null,
    onFinancialDataStringsChanged: (fd: FinancialDataStrings) => void,
    onFinalizationDataChanged: (fd: OverviewData | null) => void,
    onPaymentOptionsChanged: (fd: PaymentOptions) => void,
    onDeliveryOptionsChanged: (fd: DeliveryOptions) => void,
}) {
    const orderContext = useContext(OrderContext);
    const order = orderContext.order;
    const fetch = useContext(AppConfigContext).fetch;
    const cart = useContext(CartContext);

    const [finalizationData, setFinalizationData] = useState<OverviewData | null>(null);
    const [financialData, setFinancialData] = useState<FinancialData>({
        cartPrice: 0,
        deliveryPrice: 0,
        // Todo: Apply discount code
        cartDiscount: 0,
        tax: 0,
        ipgWage: 0,
        totalOrderPrice: 0,
        packingPrice: 0,
        discountCodeAmount: 0,
        totalOrderPriceConsideringIpgWage: 0
    });


    useEffect(() => {
        setFinancialData({
            cartPrice: cart.allItemsPriceNoDiscount ?? 0,
            deliveryPrice: order?.deliveryPrice ?? 0,
            // Todo: Apply discount code
            cartDiscount: cart.allItemsPriceNoDiscount - cart.allItemsPrice,
            tax: order?.taxPrice ?? 0,
            ipgWage: (props.selectedPaymentMethod == "ipg") ? (finalizationData?.ipgWage ?? 0) : 0,
            totalOrderPrice: order?.totalPayablePrice ?? 0,
            packingPrice: order?.totalPackingPrice ?? 0,
            discountCodeAmount: order?.codeDiscountAmount ?? 0,
            totalOrderPriceConsideringIpgWage:  (order?.totalPayablePrice ?? 0) + ((props.selectedPaymentMethod == "ipg") ? (finalizationData?.ipgWage ?? 0) : 0)
        });
    }, [cart, props.selectedPaymentMethod, order, finalizationData,]);

    useEffect(() => {
        fetch.Order.requests.getFinalizationData().then(d => setFinalizationData(d))
    }, []);

    useEffect(() => {
        setFinalizationData(orderContext.overviewData);
    }, [orderContext.overviewData]);

    useEffect(() => {
        props.onDeliveryOptionsChanged([
            {
                tag: "express",
                description: `هزینه ارسال با پیک رستوران: ${finalizationData?.deliveryOptions.express.price}`,
                title: "ارسال با پیک",
                visible: finalizationData?.deliveryOptions.express.available ?? false
            },
            {
                tag: "manual",
                description: "هزینه ارسال توسط شما پرداخت خواهد شد, فقط پرداخت آنلاین",
                title: "ارسال با آژانس",
                visible: finalizationData?.deliveryOptions.manualService.available ?? false
            },
            {
                tag: "personal",
                description: "بدون هزینه",
                title: "تحویل حضوری از رستوران",
                visible: finalizationData?.deliveryOptions.personal.available ?? false
            },
        ]);
    }, [orderContext.order, finalizationData]);


    useEffect(() => {
        props.onFinalizationDataChanged(finalizationData);
    }, [financialData]);

    useEffect(() => {
        props.onPaymentOptionsChanged([
            {
                tag: "ipg",
                title: "پرداخت آنلاین",
                visible: finalizationData?.paymentOptions.ipg.available ?? false,
                description: `کارمزد: ${finalizationData?.paymentOptions.ipg.wage} درصد`,
                icon: "https://storage.meezban.net/pay-icons/online.png"
            },
            {
                tag: "ctc",
                title: "کارت به کارت",
                visible: finalizationData?.paymentOptions.ctc.available ?? false,
                description: "",
                icon: "https://storage.meezban.net/pay-icons/card.png"
            },
            {
                tag: "pos",
                title: "پرداخت حضوری",
                visible: finalizationData?.paymentOptions.pos.available ?? false,
                description: "نقدی یا دستگاه POS",
                icon: "https://storage.meezban.net/pay-icons/pos.png"
            },
        ]);
    }, [finalizationData]);

    useEffect(() => {
        props.onFinancialDataStringsChanged({
            rows: [
                {
                    title: "مجموع اقلام",
                    price: `${threeDigitsSep(financialData.cartPrice)} تومان`,
                    visible: true
                },
                {
                    title: "هزینه ارسال",
                    price: `${threeDigitsSep(financialData.deliveryPrice)} تومان`,
                    visible: financialData.deliveryPrice != 0
                },
                {
                    title: "مالیات",
                    price: `${threeDigitsSep(financialData.tax)} تومان`,
                    visible: financialData.tax != 0
                },
                {
                    title: "مجموع تخفیف کالا",
                    price: `${threeDigitsSep(financialData.cartDiscount)} تومان`,
                    visible: financialData.cartDiscount != 0
                },
                {
                    title: "میزان کد تخفیف",
                    price: `${threeDigitsSep(financialData.discountCodeAmount)} تومان`,
                    visible: financialData.discountCodeAmount != 0
                },
                {
                    title: "هزینه بسته بندی",
                    price: `${threeDigitsSep(financialData.packingPrice)} تومان`,
                    visible: financialData.packingPrice != 0
                },
                {
                    title: "کارمزد درگاه پرداخت",
                    price: `${threeDigitsSep(financialData.ipgWage)} تومان`,
                    visible: financialData.ipgWage != 0
                }
            ],
            totalPayablePrice: `${threeDigitsSep(financialData.totalOrderPriceConsideringIpgWage)} تومان`
        });
    }, [financialData]);

    return <>
        {props.children}
    </>
}